import React from 'react'
import { MakeTranslateReturnValue } from '@ds/utils/localization/format'
import { Icon, l } from '@manychat/manyui'

import { ProductType } from 'common/core/interfaces/products'

export interface SuccessModalFeature {
  title: MakeTranslateReturnValue
  icons?: React.ReactElement[]
  preIcon: React.ReactElement
  tooltip?: MakeTranslateReturnValue
  note?: React.ReactNode
}

export interface Feature {
  title: MakeTranslateReturnValue
  icon: React.ReactElement
  tooltip?: MakeTranslateReturnValue | string
  note?: MakeTranslateReturnValue | string
  value: MakeTranslateReturnValue | string
}

const ACTIVE_CONTACTS_TOOLTIP = l.makeTranslate(
  'People who’ve actually interacted with you (messaged you or triggered one of your automations) during the billing period.',
)

export const INBOX_SEATS_FEATURE = {
  [ProductType.ESSENTIAL]: 1,
  [ProductType.PRO]: 2,
  [ProductType.BUSINESS]: 3,
  [ProductType.ADVANCED]: 3,
}

const INBOX_SEATS_TOOLTIPS = {
  [ProductType.ESSENTIAL]: l.makeTranslate(
    'Organize conversations with a "favorite" label, set reminders, or schedule replies. Includes {inbox} Inbox seats.',
    { inbox: INBOX_SEATS_FEATURE[ProductType.ESSENTIAL] },
  ),
  [ProductType.PRO]: l.makeTranslate(
    'Organize conversations with a "favorite" label, set reminders, or schedule replies. Includes {inbox} Inbox seats.',
    { inbox: INBOX_SEATS_FEATURE[ProductType.PRO] },
  ),
  [ProductType.BUSINESS]: l.makeTranslate(
    'Organize conversations with a "favorite" label, set reminders, or schedule replies. Includes {inbox} Inbox seats.',
    { inbox: INBOX_SEATS_FEATURE[ProductType.BUSINESS] },
  ),
  [ProductType.ADVANCED]: l.makeTranslate(
    'Organize conversations with a "favorite" label, set reminders, or schedule replies. Includes {inbox} Inbox seats.',
    { inbox: INBOX_SEATS_FEATURE[ProductType.ADVANCED] },
  ),
}

const INBOX_FEATURE = {
  [ProductType.ESSENTIAL]: {
    title: l.makeTranslate('Inbox'),
    tooltip: INBOX_SEATS_TOOLTIPS[ProductType.ESSENTIAL],
    preIcon: <Icon.Inbox size={14} aria-label="Inbox" />,
  },
  [ProductType.PRO]: {
    title: l.makeTranslate('Inbox'),
    tooltip: INBOX_SEATS_TOOLTIPS[ProductType.PRO],
    preIcon: <Icon.Inbox size={14} aria-label="Inbox" />,
  },
  [ProductType.BUSINESS]: {
    title: l.makeTranslate('Inbox'),
    tooltip: INBOX_SEATS_TOOLTIPS[ProductType.BUSINESS],
    preIcon: <Icon.Inbox size={14} aria-label="Inbox" />,
  },
  [ProductType.ADVANCED]: {
    title: l.makeTranslate('Inbox'),
    tooltip: INBOX_SEATS_TOOLTIPS[ProductType.ADVANCED],
    preIcon: <Icon.Inbox size={14} aria-label="Inbox" />,
  },
}

const activeContactsFeature = {
  [ProductType.ESSENTIAL]: {
    title: l.makeTranslate('Up to 250 Active contacts/mo'),
    tooltip: ACTIVE_CONTACTS_TOOLTIP,
    preIcon: <Icon.User size={14} aria-label="Active contacts" />,
  },
  [ProductType.PRO]: {
    title: l.makeTranslate('Up to 2,500 Active contacts/mo'),
    tooltip: ACTIVE_CONTACTS_TOOLTIP,
    preIcon: <Icon.User size={14} aria-label="Active contacts" />,
  },
  [ProductType.BUSINESS]: {
    title: l.makeTranslate('Up to 7,500 Active contacts/mo'),
    tooltip: ACTIVE_CONTACTS_TOOLTIP,
    preIcon: <Icon.User size={14} aria-label="Active contacts" />,
  },
  [ProductType.ADVANCED]: {
    title: l.makeTranslate('Up to 25,000 Active contacts/mo'),
    tooltip: ACTIVE_CONTACTS_TOOLTIP,
    preIcon: <Icon.User size={14} aria-label="Active contacts" />,
  },
}

const TWO_CHANNELS_FEATURE = {
  title: l.makeTranslate('2 channels'),
  preIcon: <Icon.MessageOpen size={14} aria-label="Channels" />,
  icons: [
    <Icon.TelegramChannel size={16} aria-label="Telegram channel" />,
    <Icon.TikTokChannel size={16} aria-label="TikTok channel" />,
    <Icon.MessengerChannel size={16} aria-label="Facebook messenger channel" />,
    <Icon.InstagramChannel size={16} aria-label="Instagram channel" />,
  ],
}

const THREE_CHANNELS_FEATURE = {
  title: l.makeTranslate('3 channels'),
  preIcon: <Icon.MessageOpen size={14} aria-label="Channels" />,
  icons: [
    <Icon.SmsChannel size={16} aria-label="SMS channel" />,
    <Icon.EmailChannel size={16} aria-label="Email channel" />,
    <Icon.WhatsAppChannel size={16} aria-label="WhatsApp channel" />,
    <Icon.TelegramChannel size={16} aria-label="Telegram channel" />,
    <Icon.TikTokChannel size={16} aria-label="TikTok channel" />,
    <Icon.MessengerChannel size={16} aria-label="Facebook messenger channel" />,
    <Icon.InstagramChannel size={16} aria-label="Instagram channel" />,
  ],
}

const UNLIMITED_CHANNELS_FEATURE = {
  title: l.makeTranslate('Unlimited channels'),
  preIcon: <Icon.MessageOpen size={14} aria-label="Channels" />,
}

const ADVANCED_AUTOMATIONS_FEATURE = {
  title: l.makeTranslate('Advanced automations'),
  tooltip: l.makeTranslate('Broadcasts and AI-driven conversations'),
  preIcon: <Icon.Automation size={14} aria-label="Advanced automations" />,
}

const CHANNELS_FEATURES = {
  free: TWO_CHANNELS_FEATURE,
  [ProductType.ESSENTIAL]: TWO_CHANNELS_FEATURE,
  [ProductType.PRO]: THREE_CHANNELS_FEATURE,
  [ProductType.BUSINESS]: UNLIMITED_CHANNELS_FEATURE,
  [ProductType.ADVANCED]: UNLIMITED_CHANNELS_FEATURE,
}

const AI_FEATURE = {
  title: l.makeTranslate('AI'),
  tooltip: l.makeTranslate(
    'Teach AI about your business and tone of voice and let it respond to your DMs, answer FAQs, reply to positive comments, and guide conversations towards your goals, automatically.',
  ),
  preIcon: <Icon.Ai className="b-a-0" size={12} aria-hidden="true" />,
}

const ESSENTIAL_FEATURES = [
  activeContactsFeature[ProductType.ESSENTIAL],
  CHANNELS_FEATURES[ProductType.ESSENTIAL],
  {
    title: l.makeTranslate('Unlimited custom automations'),
    preIcon: <Icon.Automation size={14} aria-label="Unlimited custom automations" />,
  },
  INBOX_FEATURE[ProductType.ESSENTIAL],
]

const PRO_FEATURES = [
  activeContactsFeature[ProductType.PRO],
  CHANNELS_FEATURES[ProductType.PRO],
  INBOX_FEATURE[ProductType.PRO],
  AI_FEATURE,
]

const BUSINESS_FEATURES = [
  activeContactsFeature[ProductType.BUSINESS],
  CHANNELS_FEATURES[ProductType.BUSINESS],
  ADVANCED_AUTOMATIONS_FEATURE,
  INBOX_FEATURE[ProductType.BUSINESS],
  AI_FEATURE,
]

const ADVANCED_FEATURES = [
  activeContactsFeature[ProductType.ADVANCED],
  CHANNELS_FEATURES[ProductType.ADVANCED],
  ADVANCED_AUTOMATIONS_FEATURE,
  INBOX_FEATURE[ProductType.ADVANCED],
  AI_FEATURE,
]

export const FeaturesByTier: Partial<Record<ProductType, SuccessModalFeature[]>> = {
  [ProductType.ESSENTIAL]: ESSENTIAL_FEATURES,
  [ProductType.PRO]: PRO_FEATURES,
  [ProductType.BUSINESS]: BUSINESS_FEATURES,
  [ProductType.ADVANCED]: ADVANCED_FEATURES,
}

type PriceBreakdownRowContent = Pick<Feature, 'value' | 'tooltip' | 'note'>

interface PriceBreakdownContent {
  storedContacts: PriceBreakdownRowContent
  activeContacts: PriceBreakdownRowContent
  ai: PriceBreakdownRowContent
  inbox: PriceBreakdownRowContent
}

export const createPriceBreakdownArray = ({
  storedContacts,
  activeContacts,
  ai,
  inbox,
}: PriceBreakdownContent) => {
  return [
    {
      title: l.makeTranslate('Stored contacts'),
      icon: <Icon.User className="b-a-0" size={12} aria-label={l.translate('User')} />,
      ...storedContacts,
    },
    {
      title: l.makeTranslate('Active Contacts/mo'),
      icon: <Icon.User className="b-a-0" size={12} aria-label={l.translate('User')} />,
      ...activeContacts,
    },
    {
      title: l.makeTranslate('AI features'),
      icon: <Icon.Ai className="b-a-0" size={12} aria-label={l.translate('AI')} />,
      tooltip: AI_FEATURE.tooltip,
      ...ai,
    },
    {
      title: l.makeTranslate('Inbox'),
      icon: <Icon.Inbox className="b-a-0" size={12} aria-label={l.translate('Inbox')} />,
      ...inbox,
    },
  ] satisfies Feature[]
}
